import { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  List,
  Popover,
  Progress,
  Row,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  AimOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileSearchOutlined,
  LockOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useEvalRoleLabels } from '../contexts/EvalRoleLabelsContext';
import { taskStatusColor, taskStatusLabel } from '../utils/taskStatusDisplay';
import { RulesPanel, type RuleItem } from '../components/RulesPanel';
import { useIsMobile } from '../hooks/useMobile';

/** 评价规则文案。仅在列表页展示一次，进入具体打分页不再重复。 */
const EVAL_RULES: RuleItem[] = [
  {
    title: '通关式打分，不允许越级',
    desc: '从最低档开始逐级判断 —— 只有满足了低档的全部要求，才能进入下一档。高分必须覆盖低分的所有要求。如果觉得某一档还没完全做到，就停在当前档位，不要往上走。',
    color: '#722ed1',
    icon: <AimOutlined />,
  },
  {
    title: '基于事实，确保真实',
    desc: '每一项打分都应以你亲身观察到的具体工作行为和事件为依据，而非道听途说或笼统印象。对某位同事在某维度上接触不足时，不要凭猜测给分。真实的反馈才是对被评价人最大的尊重和帮助。',
    color: '#1677ff',
    icon: <FileSearchOutlined />,
  },
  {
    title: '独立判断，保持客观',
    desc: '基于该维度的实际表现独立打分，避免因个人关系拔高或压低；避免"老好人式"全打高分或"泄私愤式"恶意打低分；不要受其他同事意见左右。每个维度单独评判，避免光环效应（一项突出连带拉高）与角效应（一项不足全盘否定）。',
    color: '#13c2c2',
    icon: <BulbOutlined />,
  },
  {
    title: '保密原则',
    desc: '你的评价内容和打分将严格保密，请放心如实作答。评价前后不要与其他同事讨论、对照或统一打分，确保每份评价都是你个人的独立判断。',
    color: '#fa8c16',
    icon: <LockOutlined />,
  },
];

/** 列表行「被评价人」Popover 内容与打分页对齐 */
function EvaluateeTaskPopoverContent({ row }: { row: any }) {
  const { getEvalRoleLabel } = useEvalRoleLabels();
  const e = row.evaluatee ?? {};
  return (
    <div style={{ maxWidth: 300, whiteSpace: 'normal', lineHeight: 1.65, fontSize: 13 }}>
      <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 8 }}>素质评价 · 被评价人</div>
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 15 }}>{e.name ?? '—'}</div>
      <div>部门：{e.deptPath ?? '—'}</div>
      <div>职位：{e.position ?? '—'}</div>
      <div style={{ marginTop: 10, fontSize: 12, color: '#595959' }}>
        评价角色：<Tag style={{ margin: 0 }}>
          {getEvalRoleLabel(row.template?.pool, row.roleInEval)}
        </Tag>
      </div>
    </div>
  );
}

/**
 * 素质项评价 · 我的任务列表
 *
 * 顶部布局：
 *   1) 统计概览（共需评价 / 已完成 / 待完成 / 完成率进度条）
 *   2) 评价须知 RulesPanel（4 条，可折叠；进入具体打分页不再重复）
 *   3) 任务表格
 */
export function MyEvalTasksPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { getEvalRoleLabel } = useEvalRoleLabels();

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.get('/my/eval-tasks'),
  });

  const list = tasks as any[];
  const pendingCount = list.filter((t) => t.status !== 'SUBMITTED').length;
  const doneCount = list.length - pendingCount;
  const total = list.length;
  const percent = total === 0 ? 0 : Math.round((doneCount / total) * 100);

  const [activeTab, setActiveTab] = useState('pending');
  const displayList = list.filter((t) =>
    activeTab === 'pending' ? t.status !== 'SUBMITTED' : t.status === 'SUBMITTED'
  );

  return (
    <div>
      <div className="tg-page-title" style={{ marginBottom: 8 }}>
        素质项评价
      </div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        多角色对同事的素质维度打分（评价关系由 HR 配置）。
      </Typography.Paragraph>

      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: 16 } }}>
        <Row gutter={16} align="middle">
          <Col xs={24} md={16}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title={
                    <span>
                      <UnorderedListOutlined style={{ marginRight: 6 }} />
                      共需评价
                    </span>
                  }
                  value={total}
                  suffix="人"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={
                    <span>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                      已完成
                    </span>
                  }
                  value={doneCount}
                  valueStyle={{ color: '#52c41a' }}
                  suffix="人"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={
                    <span>
                      <ClockCircleOutlined
                        style={{ color: pendingCount ? '#fa8c16' : '#999', marginRight: 6 }}
                      />
                      待完成
                    </span>
                  }
                  value={pendingCount}
                  valueStyle={{ color: pendingCount ? '#fa8c16' : '#999' }}
                  suffix="人"
                />
              </Col>
            </Row>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ paddingLeft: 12 }}>
              <div style={{ marginBottom: 4, color: '#666', fontSize: 13 }}>完成进度</div>
              <Progress
                percent={percent}
                strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                format={(p) => `${p}%`}
              />
            </div>
          </Col>
        </Row>
      </Card>

      <RulesPanel rules={EVAL_RULES} />

      <Card styles={{ body: { padding: isMobile ? 12 : 24 } }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'pending', label: `待评价 (${pendingCount})` },
            { key: 'submitted', label: `已评价 (${doneCount})` },
          ]}
          style={{ marginBottom: 16 }}
        />
        {isMobile ? (
          <List
            loading={isLoading}
            dataSource={displayList}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="暂无素质项评价任务"
                />
              ),
            }}
            split={false}
            pagination={displayList.length > 8 ? { pageSize: 8, showTotal: (n) => `共 ${n} 项` } : false}
            renderItem={(r: any) => {
              const submitted = r.status === 'SUBMITTED';
              const dept = r.evaluatee?.deptPath?.split('/').slice(-2).join(' / ') ?? '—';
              const position = r.evaluatee?.position ?? '—';
              return (
                <List.Item style={{ padding: '6px 0', borderBottom: 'none' }}>
                  <Card
                    size="small"
                    style={{
                      width: '100%',
                      borderRadius: 12,
                      border: '1px solid rgba(124,92,191,0.15)',
                      boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
                    }}
                    styles={{ body: { padding: 14 } }}
                    onClick={() => navigate(`/eval/${r.id}`)}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 8,
                        gap: 8,
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 16,
                            color: '#1f1f1f',
                            lineHeight: 1.3,
                          }}
                        >
                          {r.evaluatee?.name ?? '—'}
                        </div>
                        <div
                          style={{
                            color: '#94a3b8',
                            fontSize: 12,
                            marginTop: 4,
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                          }}
                        >
                          {dept} · {position}
                        </div>
                      </div>
                      <Tag color={taskStatusColor(r.status)} style={{ marginRight: 0, flexShrink: 0 }}>
                        {taskStatusLabel(r.status)}
                      </Tag>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <Tag color="purple" style={{ marginRight: 0 }}>
                        {getEvalRoleLabel(r.template?.pool, r.roleInEval)}
                      </Tag>
                    </div>
                    <Button
                      block
                      type={submitted ? 'default' : 'primary'}
                      size="large"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/eval/${r.id}`);
                      }}
                    >
                      {submitted ? '查看打分' : '去打分'}
                    </Button>
                  </Card>
                </List.Item>
              );
            }}
          />
        ) : (
          <Table
            rowKey={(r: any) => r.id}
            loading={isLoading}
            dataSource={displayList}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="暂无素质项评价任务"
                />
              ),
            }}
            pagination={{ pageSize: 10, showTotal: (n) => `共 ${n} 项` }}
            columns={[
              {
                title: '被评价人',
                width: 110,
                render: (_: unknown, row: any) => (
                  <Popover
                    zIndex={4200}
                    trigger={['hover', 'click']}
                    placement="bottomLeft"
                    mouseEnterDelay={0.12}
                    getPopupContainer={() => document.body}
                    content={<EvaluateeTaskPopoverContent row={row} />}
                  >
                    <span
                      style={{
                        cursor: 'pointer',
                        color: '#1677ff',
                        borderBottom: '1px dashed rgba(22,119,255,0.55)',
                      }}
                    >
                      {row.evaluatee?.name ?? '—'}
                    </span>
                  </Popover>
                ),
              },
              {
                title: '部门',
                dataIndex: ['evaluatee', 'deptPath'],
                width: 220,
                ellipsis: true,
                render: (v?: string) =>
                  v ? <Typography.Text title={v}>{v.split('/').slice(-2).join(' / ')}</Typography.Text> : '-',
              },
              {
                title: '职位',
                dataIndex: ['evaluatee', 'position'],
                width: 160,
                ellipsis: true,
              },
              {
                title: '评价角色',
                width: 160,
                render: (_: any, r: any) => (
                  <Tag>{getEvalRoleLabel(r.template?.pool, r.roleInEval)}</Tag>
                ),
              },
              {
                title: '状态',
                dataIndex: 'status',
                width: 100,
                render: (v: string) => (
                  <Tag color={taskStatusColor(v)}>{taskStatusLabel(v)}</Tag>
                ),
              },
              {
                title: '操作',
                width: 100,
                render: (_: any, r: any) => (
                  <Button size="small" onClick={() => navigate(`/eval/${r.id}`)}>
                    {r.status === 'SUBMITTED' ? '查看' : '去打分'}
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
}
